import { useLocation } from 'react-router-dom'

/**
 * DRAFT — legal content pending review by Daniel Suchan / MUNI legal
 * before this route is advertised.
 *
 * Terms of Service — modeled on ./privacy.tsx (same bilingual split,
 * styling and route registration). Reachable at /terms (English) and
 * /cs/terms (Czech); linked from the app and auth footers alongside the
 * privacy notice.
 *
 * Keep it terse and accurate — the service is an academic accessibility
 * accommodation operated by Středisko Teiresiás, Masaryk University, not
 * a commercial product.
 */
export function TermsPage() {
	const location = useLocation()
	const isCzech = location.pathname.startsWith('/cs/')

	return (
		<>
			{/* DRAFT — legal content pending review by Daniel Suchan / MUNI legal
			    before this route is advertised. */}
			{isCzech ? <TermsCs /> : <TermsEn />}
		</>
	)
}

function TermsEn() {
	return (
		<article className="prose prose-slate mx-auto max-w-2xl px-6 py-12">
			<h1>Terms of Service — Polygraf Online</h1>
			<p className="text-sm opacity-70">
				Last updated: 2026-07-03 · <a href="/cs/terms">Česky</a>
			</p>

			<h2>About the service</h2>
			<p>
				Polygraf Online is an accessibility service operated by Středisko Teiresiás, Masaryk
				University (Masarykova univerzita, Žerotínovo nám. 617/9, 601 77 Brno, IČO 00216224). It
				provides real-time speech-to-text transcription and presentation streaming, primarily for
				students and staff with hearing or vision impairments. Day-to-day operations are handled by
				Blaze; technical contact: Daniel Suchan (<code>suchan@blaze.codes</code>).
			</p>

			<h2>Agreement to these terms</h2>
			<p>
				By creating an account or using Polygraf Online you agree to these Terms of Service and
				confirm that you have read the <a href="/privacy">Privacy Notice</a>. If you do not agree,
				do not use the service.
			</p>

			<h2>What the service provides</h2>
			<ul>
				<li>
					Real-time transcripts of spoken content, produced by human writers or automatic speech
					recognition.
				</li>
				<li>Streaming of presentation and screen media to session viewers.</li>
				<li>
					Spaces, rooms and sessions with controlled access (public, signed-in users, members only,
					or restricted to user IPs/domains).
				</li>
			</ul>
			<p>
				The service is intended for academic accessibility use at Masaryk University and partner
				institutions.
			</p>

			<h2>Accounts and sign-in</h2>
			<ul>
				<li>
					You can sign in with an email address and password, or through single sign-on (the
					eduID.cz academic federation, MUNI, Google, Microsoft, or Apple).
				</li>
				<li>
					Keep your credentials confidential. You are responsible for activity that happens under
					your account.
				</li>
				<li>
					Institutional accounts remain subject to the rules of your home institution in addition to
					these terms.
				</li>
				<li>Information you provide about yourself must be accurate.</li>
			</ul>

			<h2>Acceptable use</h2>
			<p>You agree to:</p>
			<ul>
				<li>
					use the service only for its accessibility purpose and in compliance with applicable law;
				</li>
				<li>not access sessions, spaces, or recordings that you are not authorised to access;</li>
				<li>
					not redistribute transcripts, streamed lectures, or recordings beyond the permissions
					granted by the lecturer or institution;
				</li>
				<li>
					not disrupt or overload the service, and not probe or circumvent its security or access
					restrictions;
				</li>
				<li>not impersonate others or misrepresent your institutional affiliation;</li>
				<li>not submit unlawful content.</li>
			</ul>
			<p>We may restrict or suspend access that violates these rules.</p>

			<h2>Content and intellectual property</h2>
			<ul>
				<li>
					Lectures, presentations, and other source content remain the property of their authors or
					the university.
				</li>
				<li>
					Transcripts are produced as an accessibility accommodation derived from that content;
					rights to the underlying content remain with the original rights holders. You may use
					transcripts only for the purpose for which access was granted to you.
				</li>
				<li>
					By submitting content to the service you grant Masaryk University a limited, non-exclusive
					licence to process, store, and transmit it, solely to provide the service.
				</li>
				<li>
					The Polygraf Online software and branding are the property of Masaryk University or its
					licensors; these terms grant you no rights to them beyond using the service.
				</li>
			</ul>

			<h2>Availability and disclaimers</h2>
			<ul>
				<li>
					The service is provided “as is” and “as available”. We may modify it, suspend it
					temporarily (for example for maintenance), or discontinue it.
				</li>
				<li>
					Transcripts are produced in real time (by a human writer or automatically) and may contain
					errors or omissions. They are not an official or authoritative record of a lecture or
					examination unless the institution expressly designates them as such.
				</li>
				<li>We do not guarantee uninterrupted or error-free operation.</li>
			</ul>

			<h2>Limitation of liability</h2>
			<p>
				To the extent permitted by the law of the Czech Republic, Masaryk University is not liable
				for indirect or consequential damages arising from your use of, or inability to use, the
				service. Liability that cannot be limited by law (for example for harm caused intentionally
				or for injury to health) remains unaffected.
			</p>

			<h2>Suspension and termination</h2>
			<ul>
				<li>
					We may suspend or terminate accounts that materially violate these terms; where
					reasonable, we will notify you first.
				</li>
				<li>
					You may stop using the service at any time and delete your account from your profile
					settings.
				</li>
			</ul>

			<h2>Privacy</h2>
			<p>
				How we handle personal data is described in the <a href="/privacy">Privacy Notice</a>.
			</p>

			<h2>Changes to these terms</h2>
			<p>
				We will publish material changes here at least 14 days before they take effect. By
				continuing to use the service after a change takes effect you accept the updated terms. The
				current version is always the one published at{' '}
				<a href="https://polygraf.app/terms">https://polygraf.app/terms</a>.
			</p>

			<h2>Governing law</h2>
			<p>
				These terms are governed by the law of the Czech Republic, and any disputes fall under the
				jurisdiction of the courts of the Czech Republic. Your mandatory statutory rights are not
				affected.
			</p>

			<h2>Contact</h2>
			<p>
				Questions about these terms: Daniel Suchan (<code>suchan@blaze.codes</code>).
			</p>
		</article>
	)
}

function TermsCs() {
	return (
		<article className="prose prose-slate mx-auto max-w-2xl px-6 py-12">
			<h1>Podmínky užití — Polygraf Online</h1>
			<p className="text-sm opacity-70">
				Aktualizováno: 2026-07-03 · <a href="/terms">English</a>
			</p>

			<h2>O službě</h2>
			<p>
				Polygraf Online provozuje Středisko Teiresiás Masarykovy univerzity (Masarykova univerzita,
				Žerotínovo nám. 617/9, 601 77 Brno, IČO 00216224). Služba poskytuje přepis řeči v reálném
				čase a streamování prezentací, primárně pro studenty a zaměstnance se sluchovým nebo
				zrakovým postižením. Provoz zajišťuje firma Blaze; technický kontakt: Daniel Suchan (
				<code>suchan@blaze.codes</code>).
			</p>

			<h2>Souhlas s podmínkami</h2>
			<p>
				Vytvořením účtu nebo používáním služby Polygraf Online souhlasíte s těmito podmínkami užití
				a potvrzujete, že jste se seznámili se <a href="/cs/privacy">zásadami ochrany soukromí</a>.
				Pokud s podmínkami nesouhlasíte, službu nepoužívejte.
			</p>

			<h2>Co služba poskytuje</h2>
			<ul>
				<li>
					Přepis mluveného slova v reálném čase, vytvářený přepisovateli nebo automatickým
					rozpoznáváním řeči.
				</li>
				<li>Streamování prezentací a obrazu divákům relace.</li>
				<li>
					Prostory, místnosti a relace s řízeným přístupem (veřejné, pro přihlášené, pouze pro
					členy, nebo omezené na IP/domény uživatelů).
				</li>
			</ul>
			<p>
				Služba je určena pro akademické účely zpřístupnění výuky na Masarykově univerzitě a
				partnerských institucích.
			</p>

			<h2>Účty a přihlašování</h2>
			<ul>
				<li>
					Přihlásit se můžete e-mailem a heslem, nebo přes jednotné přihlášení (akademická federace
					eduID.cz, MUNI, Google, Microsoft nebo Apple).
				</li>
				<li>Přihlašovací údaje chraňte. Odpovídáte za aktivitu, která pod vaším účtem proběhne.</li>
				<li>
					Institucionální účty podléhají vedle těchto podmínek i pravidlům vaší domovské instituce.
				</li>
				<li>Údaje, které o sobě uvedete, musí být pravdivé.</li>
			</ul>

			<h2>Pravidla užívání</h2>
			<p>Zavazujete se:</p>
			<ul>
				<li>
					používat službu pouze k jejímu účelu zpřístupnění výuky a v souladu s právními předpisy;
				</li>
				<li>nepřistupovat k relacím, prostorům ani záznamům, k nimž nemáte oprávnění;</li>
				<li>
					nešířit přepisy, streamované přednášky ani záznamy nad rámec oprávnění uděleného
					přednášejícím nebo institucí;
				</li>
				<li>
					nenarušovat a nepřetěžovat provoz služby a neobcházet její zabezpečení ani omezení
					přístupu;
				</li>
				<li>nevydávat se za jiné osoby a neuvádět nepravdivou institucionální příslušnost;</li>
				<li>nenahrávat protiprávní obsah.</li>
			</ul>
			<p>Přístup, který tato pravidla porušuje, můžeme omezit nebo pozastavit.</p>

			<h2>Obsah a duševní vlastnictví</h2>
			<ul>
				<li>
					Přednášky, prezentace a další zdrojový obsah zůstávají vlastnictvím svých autorů, resp.
					univerzity.
				</li>
				<li>
					Přepisy vznikají jako podpůrné opatření pro zpřístupnění výuky; práva k podkladovému
					obsahu zůstávají původním nositelům práv. Přepisy smíte užívat jen pro účel, pro který vám
					byl přístup udělen.
				</li>
				<li>
					Nahráním obsahu do služby udělujete Masarykově univerzitě omezenou, nevýhradní licenci k
					jeho zpracování, uložení a přenosu, a to výhradně za účelem poskytování služby.
				</li>
				<li>
					Software a označení Polygraf Online jsou majetkem Masarykovy univerzity, resp. jejích
					poskytovatelů licencí; tyto podmínky vám k nim nedávají žádná práva nad rámec užívání
					služby.
				</li>
			</ul>

			<h2>Dostupnost a vyloučení záruk</h2>
			<ul>
				<li>
					Služba je poskytována „tak, jak je“ a „jak je dostupná“. Můžeme ji upravovat, dočasně
					pozastavit (např. kvůli údržbě) nebo ukončit.
				</li>
				<li>
					Přepisy vznikají v reálném čase (lidským přepisovatelem nebo automaticky) a mohou
					obsahovat chyby či výpadky. Nejsou úředním ani autoritativním záznamem výuky nebo zkoušky,
					pokud je tak instituce výslovně neoznačí.
				</li>
				<li>Nezaručujeme nepřetržitý ani bezchybný provoz.</li>
			</ul>

			<h2>Omezení odpovědnosti</h2>
			<p>
				V rozsahu, v jakém to připouštějí právní předpisy České republiky, neodpovídá Masarykova
				univerzita za nepřímé či následné škody vzniklé užíváním služby nebo nemožností ji užívat.
				Odpovědnost, kterou podle zákona omezit nelze (např. za újmu způsobenou úmyslně nebo za újmu
				na zdraví), zůstává nedotčena.
			</p>

			<h2>Pozastavení a zrušení účtu</h2>
			<ul>
				<li>
					Účty, které závažně porušují tyto podmínky, můžeme pozastavit nebo zrušit; je-li to
					přiměřené, předem vás upozorníme.
				</li>
				<li>Službu můžete kdykoli přestat používat a svůj účet smazat v nastavení profilu.</li>
			</ul>

			<h2>Ochrana osobních údajů</h2>
			<p>
				Zpracování osobních údajů popisují <a href="/cs/privacy">zásady ochrany soukromí</a>.
			</p>

			<h2>Změny podmínek</h2>
			<p>
				Podstatné změny zveřejníme alespoň 14 dní před jejich účinností. Pokračováním v užívání
				služby po účinnosti změny vyjadřujete souhlas s aktualizovanými podmínkami. Aktuální verze
				je vždy ta na <a href="https://polygraf.app/cs/terms">https://polygraf.app/cs/terms</a>.
			</p>

			<h2>Rozhodné právo</h2>
			<p>
				Tyto podmínky se řídí právním řádem České republiky a k řešení sporů jsou příslušné soudy
				České republiky. Vaše zákonná práva tím nejsou dotčena.
			</p>

			<h2>Kontakt</h2>
			<p>
				S dotazy k těmto podmínkám se obraťte na Daniela Suchana (<code>suchan@blaze.codes</code>).
			</p>
		</article>
	)
}

export default TermsPage
